// src/pages/seller/ProductManagementPage.jsx
import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useApp } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';

const ProductManagementPage = () => {
  const { user } = useApp();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(collection(db, 'products'), where('sellerId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [user?.uid]);

  // ✅ 상태 업데이트 시 로컬 상태도 즉시 반영 (Optimistic UI)
  const updateProductStatus = async (productId, newStatus) => {
    try {
      // 1. 로컬 상태 먼저 업데이트 → 즉시 UI 반영
      setProducts(prev =>
        prev.map(product =>
          product.id === productId ? { ...product, status: newStatus } : product
        )
      );

      // 2. Firestore에 실제 업데이트
      await updateDoc(doc(db, 'products', productId), { status: newStatus });
    } catch (error) {
      console.error('Error updating product status:', error);
      alert('Failed to update product status');
      // 실패 시 onSnapshot이 자동으로 이전 상태로 복구하므로 별도 롤백 불필요
    }
  };

  // 상태 매핑 (UI 표시용)
  const getStatusDisplay = (status) => {
    const statusMap = {
      approved: { text: 'Active', color: 'bg-green-100 text-green-800' },
      pending: { text: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      rejected: { text: 'Inactive', color: 'bg-red-100 text-red-800' }
    };
    return statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
  };

  // Firestore에 저장되는 실제 값
  const getStatusValue = (displayStatus) => {
    if (displayStatus === 'Active') return 'approved';
    if (displayStatus === 'Pending') return 'pending';
    if (displayStatus === 'Inactive') return 'rejected';
    return 'pending';
  };

  if (user?.status === 'pending') {
    return (
      <div className="text-center py-12">
        <div className="bg-yellow-50 p-6 rounded-lg inline-block max-w-2xl">
          <h3 className="text-xl font-medium text-yellow-800 mb-2">Account Pending Approval</h3>
          <p className="text-yellow-700">
            You can view your products, but editing will be available after your account is approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium text-gray-900">Product Management</h1>
        <button 
          onClick={() => navigate('/upload')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-1"
        >
          + Add Product
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-2 text-gray-600">Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-50 p-6 rounded-lg inline-block max-w-2xl">
            <h3 className="text-xl font-medium text-gray-800 mb-2">No Products Yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't added any products yet. Start by adding your first product!
            </p>
            <button 
              onClick={() => navigate('/upload')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Your First Product
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4 text-sm text-gray-600 font-medium">
            <div>Product</div>
            <div>Stock / MOQ</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
          
          {products.map((product) => {
            const statusInfo = getStatusDisplay(product.status);
            return (
              <div key={product.id} className="grid grid-cols-4 gap-4 items-center p-4 border border-gray-200 rounded-lg">
                <div className="font-medium">{product.name}</div>
                <div>{product.stock || 0} / {product.moqPrices?.[0]?.minQty || 1}</div>
                <div>
                  <select
                    value={statusInfo.text}
                    onChange={(e) => updateProductStatus(product.id, getStatusValue(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                    disabled={user?.status !== 'active'}
                  >
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="space-x-2">
                  <button 
                    onClick={() => navigate(`/seller/edit-product/${product.id}`)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    disabled={user?.status !== 'active'}
                  >
                    Edit
                  </button>
                  <button 
                    className="text-red-600 hover:text-red-800 text-sm"
                    disabled={user?.status !== 'active'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductManagementPage;